import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQ {
  question: string;
  answer: React.ReactNode;
}
export function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs: FAQ[] = [{
    question: 'What is local business data?',
    answer: <>
          Openmart{"'"}s Local Business Data platform gives you access to a
          comprehensive database of 200M+ verified local businesses and
          decision-makers worldwide. It combines multi-source data with
          AI-powered enrichment to deliver verified emails, phone numbers,
          social profiles, and deep business intelligence — everything you need
          to find, qualify, and reach local business leads at scale.{' '}
          <a href="https://app.openmart.ai/signup" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium" data-id="element-2755">
            Start your free trial →
          </a>
        </>
  }, {
    question: 'How accurate is your data?',
    answer: 'We maintain 97–99% data accuracy through multi-source verification, AI validation, and real-time email and phone verification. Our local business database is refreshed monthly to remove outdated contacts and add new businesses, ensuring your lead data stays current and actionable.'
  }, {
    question: 'What information is included in each record?',
    answer: 'Each local business record includes 40+ data points: business name, full address, verified phone and email, website URL, social media profiles, business hours, Google ratings and reviews, decision-maker contacts, employee size, revenue estimates, tech stack usage, and more – giving you complete local business intelligence in one place.'
  }, {
    question: 'Can I export my lead lists?',
    answer: 'Yes. You can export your local business lead lists as clean CSVs ready to drop into Google Sheets, Excel, or any tool your team uses. You can also use our REST API to pull data programmatically into your own applications and workflows.'
  }, {
    question: "What's the difference between the Extractor, Database, and API?",
    answer: 'The Google Maps Extractor scrapes real-time local business data by location and category. The Local Business Database gives you instant access to 200M+ pre-verified records ready to download. The Local Business API lets you integrate business data programmatically into your own applications. All three tools access the same verified data — choose based on your workflow, or use all three.'
  }, {
    question: 'How often is the database updated?',
    answer: 'Our local business database is updated monthly with comprehensive verification passes to remove inactive businesses and refresh contact details. New businesses are also added on an ongoing basis to keep coverage current across all 500+ categories and 150+ countries.'
  }, {
    question: 'Is the data GDPR and CCPA compliant?',
    answer: 'Yes. Openmart is fully compliant with GDPR, CCPA, and international data protection regulations. All local business data is sourced from publicly available records and processed in accordance with applicable privacy laws. We provide data handling controls, consent management, and deletion capabilities to support your compliance requirements.'
  }, {
    question: 'What business categories do you cover?',
    answer: "Openmart covers 500+ local business categories across 150+ countries, including healthcare (doctors, dentists, veterinarians), legal and finance (lawyers, accountants, insurance agents), home services (plumbers, electricians, HVAC), food and beverage (restaurants, cafés, bakeries), retail, and professional services. If it's a local business, it's in our database."
  }, {
    question: 'Do you offer a free trial?',
    answer: <>
          Yes. Our Free Tier includes 200 credits per month, 20 searches, and
          access to basic local business data fields — no credit card required.
          It's the fastest way to test our data quality and see Openmart in
          action before committing to a paid plan.{' '}
          <a href="https://app.openmart.ai/signup" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium" data-id="element-2756">
            Start your free trial →
          </a>
        </>
  }, {
    question: 'How does the credit system work?',
    answer: 'Credits are used for data enrichments. Employee email lookups cost 3 credits per contact; employee phone lookups cost 9 credits per contact. Basic business information (name, address, website, business email) is available without consuming credits. Credits roll over on paid plans but reset monthly on the Free Tier.'
  }, {
    question: 'How do I access the API documentation and get an API key?',
    answer: <>
          Full local business API documentation is available at{' '}
          <a href="https://api.openmart.ai/docs" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium" data-id="element-2757">
            api.openmart.ai/docs
          </a>
          . To generate your API key, log in to your Openmart dashboard,
          navigate to API Management, and click Create first token. You can
          start making API calls immediately after.
        </>
  }, {
    question: 'Can I turn off email updates?',
    answer: "At this time, there is no self-serve option to disable email updates. Please contact our support team if you'd like to adjust your notification preferences."
  }, {
    question: 'What happens if I downgrade my plan?',
    answer: "When you downgrade, your available credits adjust to match the new plan tier. If your current usage exceeds the new plan's limits, you may receive a notification to reduce saved lists or usage accordingly. We recommend reviewing your usage before downgrading to avoid interruptions."
  }, {
    question: 'Can I use Openmart together with Clay?',
    answer: <>
          Yes. Openmart integrates directly with Clay for local business
          prospecting workflows — find and qualify local businesses in Openmart,
          then enrich and sequence them in Clay. Full setup guide:{' '}
          <a href="https://university.clay.com/claybooks/find-qualify-local-businesses-with-openmart-clay" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-700 underline font-medium" data-id="element-2758">
            Clay + Openmart Integration Guide
          </a>
        </>
  }, {
    question: "Why aren't all leads added to my saved list?",
    answer: 'Openmart automatically deduplicates your results. If multiple business locations share the same website URL, they are consolidated into a single record to keep your local business lead lists clean and free of redundant contacts.'
  }, {
    question: 'What else can I do with Openmart besides finding people?',
    answer: 'Beyond contact discovery, Openmart provides full local business intelligence: company performance data (annual revenue, average ticket size), business profile data (website, employee count, number of locations), business validation (active status, website verification), and hiring signals (job openings and growth indicators) – all in one prospecting platform.'
  }, {
    question: 'Can I share my account or invite teammates?',
    answer: 'Team access is available on the Pro plan and above. Team members can collaborate on saved lists, searches, and enrichments under the same account – ideal for sales teams running local business outreach campaigns together.'
  }, {
    question: 'Do you have data from the UK and European markets?',
    answer: "Yes. Openmart supports local business data coverage across multiple countries including the United States, Canada, Australia, New Zealand, Puerto Rico, United Kingdom, Ireland, France, Spain, Germany, Austria, Belgium, Netherlands, Denmark, Luxembourg, and Sweden. We're continuing to expand our global local business database coverage to more countries, coming soon."
  }];
  return <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2759">
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 p-8 md:p-16" data-id="element-2760">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-3xl mx-auto mb-16" data-id="element-2761">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-2762">
            Frequently asked <span className="text-blue-600" data-id="element-2763">questions</span>
          </h2>
        </motion.div>

        <div className="max-w-3xl mx-auto space-y-3" data-id="element-2764">
          {faqs.map((faq, index) => {
          const isOpen = openIndex === index;
          return <div key={index} className={`relative rounded-2xl border transition-all duration-300 overflow-hidden group ${isOpen ? 'bg-blue-50/30 border-blue-200 shadow-md shadow-blue-600/5' : 'bg-white border-slate-200 hover:border-blue-200'}`} data-id="element-2765">
                <button onClick={() => setOpenIndex(isOpen ? null : index)} className="relative z-10 w-full text-left flex justify-between items-center p-5 focus:outline-none" data-id="element-2766">
                  <span className={`font-bold transition-colors ${isOpen ? 'text-blue-600' : 'text-slate-900 group-hover:text-blue-600'}`} data-id="element-2767">
                    {faq.question}
                  </span>
                  <motion.div animate={{
                rotate: isOpen ? 180 : 0
              }} transition={{
                duration: 0.3
              }} className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ml-4 transition-colors ${isOpen ? 'bg-blue-100 text-blue-600' : 'bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-blue-600'}`} data-id="element-2768">
                    <ChevronDownIcon className="w-4 h-4" data-id="element-2769" />
                  </motion.div>
                </button>
                <AnimatePresence data-id="element-2770">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} className="overflow-hidden relative z-10" data-id="element-2771">
                      <div className="px-5 pb-5 pt-0 text-slate-600 leading-relaxed text-[15px]" data-id="element-2772">
                        {faq.answer}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>
    </section>;
}